/* PDP buy box redesign (FUEL_27). Nothing here runs without .ab-buybox-v1 on
   <html> (the A/B tool sets it, possibly after this script has run; the theme
   editor always has it).

   1. Gallery: below 992px the control gallery's Swiper switches to auto-width
      slides so the next photo peeks in (2-up strip); above it, one slide.

   2. Subscribe option: adds a label <span> inside Moon Bundles' own subscribe
      button (the app keeps its radios, state and cart building). The app's
      subscription title is split into name + saving pill, e.g.
      "Subscribe & Save - 30% OFF" -> "Subscribe & Save" + "Save 30%"; a title
      without a "- NN% OFF" ending is left as the app shows it.
      Both purchase rows also show the selected tier's price, the way the app
      prices its tier cards: one time = tier total less the tier's percent
      discount (app config), subscribe = the variant's selling plan price x
      tins, crossed out = the full tier total. The row for the active purchase
      type must equal the price the app shows on the selected tier card;
      otherwise (another discount type, a changed app setting) no row prices
      are shown, so the rows never disagree with the box.
      Moon Bundles re-renders client-side without events, so each component is
      watched with a MutationObserver (our own nodes are ignored).

   3. Spinning can: the first gallery photo shows the selected flavor's can
      video (see part 3 below).

   4. Description: the closing lines of the first tab (the text after its
      last empty line) are set in bold, as in the design. */
{
  const root = document.documentElement;
  const isOn = () => root.classList.contains('ab-buybox-v1');

  /* 1. Gallery ------------------------------------------------------------ */
  const narrow = window.matchMedia('(max-width: 991px)');

  const syncGallery = () => {
    const swiper = document.querySelector('.pdp-gallery--control .mainSwiper')?.swiper;
    if (!swiper || !isOn()) return;
    const strip = narrow.matches;
    const slidesPerView = strip ? 'auto' : 1;
    const spaceBetween = strip ? 17 : 8;
    if (swiper.params.slidesPerView === slidesPerView && swiper.params.spaceBetween === spaceBetween) return;
    swiper.params.slidesPerView = slidesPerView;
    swiper.params.spaceBetween = spaceBetween;
    if (strip) {
      for (const slide of swiper.slides) slide.style.width = '';
    }
    swiper.update();
  };

  narrow.addEventListener('change', syncGallery);
  if (document.readyState === 'complete') syncGallery();
  else window.addEventListener('load', syncGallery, { once: true });

  /* 2. Subscribe option --------------------------------------------------- */
  const OWN = 'ab-buybox-purchase';
  const TITLE_SPLIT = /^(.+?)\s*[-–—:|]\s*(\d+(?:[.,]\d+)?)\s*%\s*off\s*$/i;

  const parseJson = (text) => {
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  };

  const labels = parseJson(document.querySelector('script[data-ab-buybox-purchase]')?.textContent) ?? {};
  const configTitles = new WeakMap();

  const configTitle = (comp) => {
    if (!configTitles.has(comp)) {
      const bundle = parseJson(comp.getAttribute('bundle'));
      configTitles.set(comp, bundle?.configs?.find((c) => c.subscription?.subscribe?.title)?.subscription.subscribe.title ?? '');
    }
    return configTitles.get(comp);
  };

  /* The app's own title (its config text, or the original the subscription-copy
     test keeps when it rewrites the title), split into name + percentage. */
  const splitTitle = (comp, option) => {
    const appTitle = option.querySelector('.moonbundle-subscription-title');
    const raw = (appTitle?.dataset.abPdpSubOriginalTitle || configTitle(comp) || appTitle?.textContent || '')
      .replace(/\s+/g, ' ')
      .trim();
    const match = raw.match(TITLE_SPLIT);
    if (!match) return null;
    const percent = match[2].replace(',', '.');
    return { name: match[1], pill: labels.saving ? labels.saving.replace('{percent}', percent) : '' };
  };

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    node.className = className;
    node.textContent = text;
    return node;
  };

  /* Purchase row prices ---------------------------------------------------- */
  const variantPrices = parseJson(document.querySelector('script[data-ab-buybox-prices]')?.textContent) ?? {};
  const configOffers = new WeakMap();

  const offerById = (comp, id) => {
    if (!configOffers.has(comp)) {
      const bundle = parseJson(comp.getAttribute('bundle'));
      const offers = new Map();
      for (const config of bundle?.configs ?? []) for (const offer of config.offers ?? []) offers.set(offer.id, offer);
      configOffers.set(comp, offers);
    }
    return configOffers.get(comp).get(id);
  };

  /* "$31.46" -> 3146; the app always prints two decimals. */
  const toCents = (text) => {
    const digits = (text ?? '').replace(/\D/g, '');
    return /\d[.,]\d{2}\D*$/.test(text ?? '') && digits ? Number(digits) : NaN;
  };

  /* Cents in the app's own money format, taken from one of its prices. */
  const formatLike = (cents, sample) => {
    const decimal = /,\d{2}\D*$/.test(sample) ? ',' : '.';
    const whole = String(Math.floor(cents / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, decimal === '.' ? ',' : '.');
    return sample.replace(/\d(?:[\d.,\s]*\d)?/, `${whole}${decimal}${String(cents % 100).padStart(2, '0')}`);
  };

  const rowPrices = (comp) => {
    const card = comp.querySelector('.moonbundle-option-active');
    const offer = offerById(comp, card?.closest('[data-offer-id]')?.dataset.offerId);
    const shownText = card?.querySelector('.moonbundle-option-single-price')?.textContent.trim() ?? '';
    /* A single-variant product has no picked id in the form. */
    const variantIds = Object.keys(variantPrices);
    const variantId = document.querySelector('.product-section form[action*="/cart/add"] [name="id"]')?.value
      || (variantIds.length === 1 ? variantIds[0] : '');
    const [price, planPrice] = variantPrices[variantId] ?? [];
    /* 'none' = no tier discount (the app ignores its leftover value). */
    const percent = offer?.discount === 'none' ? 0 : offer?.discount === 'percent' ? Number(offer.discountValue) : NaN;
    if (!offer || !(percent >= 0 && percent <= 100) || !Number.isInteger(offer.qty) || !Number.isInteger(price)) return null;

    const full = price * offer.qty;
    const once = Math.round((full * (100 - percent)) / 100);
    const subscribe = Number.isInteger(planPrice) ? planPrice * offer.qty : NaN;
    const onceActive = !!comp.querySelector('.moonbundle-subscription-radio-option.active .moonbundle-onetime-title');
    if (toCents(shownText) !== (onceActive ? once : subscribe) || Number.isNaN(subscribe)) return null;

    return { full: formatLike(full, shownText), once: formatLike(once, shownText), subscribe: formatLike(subscribe, shownText) };
  };

  const priceNode = (className, text) => el('span', `${OWN}__price${className ? ` ${OWN}__price--${className}` : ''}`, text);

  const syncComponent = (comp) => {
    if (!isOn()) return;
    const options = [...comp.querySelectorAll('.moonbundle-subscription-radios .moonbundle-subscription-radio-option')];
    const option = options.find((o) => !o.querySelector('.moonbundle-onetime-title'));
    const onceOption = options.find((o) => o.querySelector('.moonbundle-onetime-title'));
    const content = option?.querySelector('.moonbundle-subscription-option-content');
    if (!content) return;
    const onceContent = onceOption?.querySelector('.moonbundle-subscription-option-content');
    const parts = splitTitle(comp, option);
    const prices = parts ? rowPrices(comp) : null;
    let span = content.querySelector(`:scope > .${OWN}`);
    let onceSpan = onceContent?.querySelector(`:scope > .${OWN}`);

    if (prices && onceContent) {
      if (onceSpan?.dataset.key !== prices.once || onceContent.lastElementChild !== onceSpan) {
        if (!onceSpan) onceSpan = document.createElement('span');
        onceSpan.className = `${OWN} ${OWN}--once`;
        onceSpan.dataset.key = prices.once;
        const offer = el('span', `${OWN}__offer`, '');
        offer.append(priceNode('', prices.once));
        onceSpan.replaceChildren(offer);
        if (onceContent.lastElementChild !== onceSpan) onceContent.append(onceSpan);
      }
    } else {
      onceSpan?.remove();
    }

    if (!parts) {
      span?.remove();
      return;
    }
    const key = `${parts.name}|${parts.pill}|${prices ? `${prices.full}|${prices.subscribe}` : ''}`;
    if (span?.dataset.key === key && content.lastElementChild === span) return;
    if (!span) span = document.createElement('span');
    span.className = OWN;
    span.dataset.key = key;
    const nodes = [el('span', `${OWN}__title`, parts.name)];
    if (parts.pill || prices) {
      const offer = el('span', `${OWN}__offer`, '');
      if (parts.pill) offer.append(el('span', `${OWN}__pill`, parts.pill));
      if (prices) {
        const was = el('s', `${OWN}__price ${OWN}__price--was`, prices.full);
        offer.append(was, priceNode('', prices.subscribe));
      }
      nodes.push(offer);
    }
    span.replaceChildren(...nodes);
    if (content.lastElementChild !== span) content.append(span);
  };

  const isOwnMutation = (mutation) => {
    const node = mutation.target.nodeType === 1 ? mutation.target : mutation.target.parentElement;
    if (node?.closest(`.${OWN}`)) return true;
    if (mutation.type !== 'childList') return false;
    const changed = [...mutation.addedNodes, ...mutation.removedNodes];
    return changed.length > 0 && changed.every((n) => n.nodeType === 1 && n.classList.contains(OWN));
  };

  const watched = new WeakSet();
  const schedulers = [];

  const watch = (comp) => {
    if (watched.has(comp)) return;
    watched.add(comp);
    let queued = false;
    const schedule = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        syncComponent(comp);
      });
    };
    new MutationObserver((mutations) => {
      if (!mutations.every(isOwnMutation)) schedule();
    }).observe(comp, { childList: true, subtree: true, characterData: true });
    schedulers.push(schedule);
    schedule();
  };

  /* A flavor pick can change the price without the app re-rendering. */
  document.addEventListener('variant:change', () => schedulers.forEach((schedule) => schedule()));

  /* 3. Spinning can ------------------------------------------------------- */
  /* The first gallery photo gives way to the selected flavor's spinning can
     (the section's "Spinning can videos" list). Safari and every iOS browser
     play VP9 without its transparency, so they get the HEVC file; the rest get
     VP9 WebM. Reduced motion keeps the still frame. A flavor without a video
     keeps the photo. */
  const canBox = document.querySelector('.pdp-gallery--control [data-ab-buybox-can]');
  const canData = parseJson(canBox?.querySelector('script')?.textContent) ?? {};
  const canVariants = new Set((canData.variants ?? []).map(String));
  const canVideos = canData.videos ?? {};
  const appleVideo = /Apple/.test(navigator.vendor);
  const stillOnly = window.matchMedia('(prefers-reduced-motion: reduce)');
  let canVideo = null;
  let canVariant = canBox?.dataset.variantId;
  let canPick = 0;

  /* The box changes only once the new flavor's still frame has loaded, so it
     never sits empty while the video downloads. */
  const whenLoaded = (url) =>
    new Promise((resolve) => {
      const still = new Image();
      still.onload = still.onerror = resolve;
      still.src = url;
    });

  const syncCan = async () => {
    if (!canBox || !isOn()) return;
    const pick = ++canPick;
    const files = canVideos[canVariant];
    if (files) await whenLoaded(files.poster);
    if (pick !== canPick) return;
    canBox.closest('.swiper-slide')?.classList.toggle('has-can-video', !!files);
    canBox.classList.toggle('is-on', !!files);
    if (!files) {
      canVideo?.pause();
      return;
    }
    if (!canVideo) {
      canVideo = document.createElement('video');
      canVideo.muted = true;
      canVideo.loop = true;
      canVideo.playsInline = true;
      canVideo.setAttribute('muted', '');
      canVideo.setAttribute('playsinline', '');
      canVideo.setAttribute('aria-hidden', 'true');
      canBox.append(canVideo);
    }
    canVideo.poster = files.poster;
    const src = stillOnly.matches ? '' : appleVideo ? files.hevc : files.webm;
    if (!src) {
      canVideo.removeAttribute('src');
      return;
    }
    if (canVideo.getAttribute('src') !== src) canVideo.src = src;
    canVideo.play().catch(() => {});
  };

  /* The theme announces every variant pick; quick add windows fire it for
     other products too, so only this product's variants count. */
  document.addEventListener('variant:change', (event) => {
    const id = String(event.detail?.variant?.id ?? '');
    if (!canVariants.has(id)) return;
    canVariant = id;
    syncCan();
  });
  stillOnly.addEventListener('change', syncCan);

  /* 4. Description -------------------------------------------------------- */
  /* The tab text is a multi-line field: lines end in <br>, an empty line is
     two <br> in a row. Everything after the last empty line goes into a
     <strong>; text with other markup is left alone. */
  const boldClosing = (text) => {
    if (!text || text.querySelector('.ab-buybox-closing')) return;
    const nodes = [...text.childNodes];
    if (nodes.some((n) => n.nodeType === 1 && n.tagName !== 'BR')) return;
    const isBreak = (n) => n?.nodeType === 1;
    const isBlank = (n) => n?.nodeType === 3 && !n.textContent.trim();
    let start = -1;
    for (let i = nodes.length - 1; i > 0; i--) {
      if (!isBreak(nodes[i])) continue;
      let j = i - 1;
      while (j >= 0 && isBlank(nodes[j])) j--;
      if (isBreak(nodes[j])) {
        start = i + 1;
        break;
      }
    }
    const closing = nodes.slice(start);
    if (start < 0 || !closing.some((n) => n.nodeType === 3 && n.textContent.trim())) return;
    const strong = document.createElement('strong');
    strong.className = 'ab-buybox-closing';
    closing[0].before(strong);
    strong.append(...closing);
  };

  /* The tabs are printed twice (desktop and phone layout); the first tab is
     the description in both. */
  const boldClosingLines = () => {
    for (const tabs of document.querySelectorAll('.product-section .product-tab-content')) {
      boldClosing(tabs.querySelector(':scope > .tab-panal .accordion__content .metafield-multi_line_text_field'));
    }
  };

  const start = () => {
    for (const comp of document.querySelectorAll('.product-section moonbundle-component')) watch(comp);
    syncGallery();
    syncCan();
    boldClosingLines();
  };

  /* The A/B tool can add the class after this deferred script has run, so
     wait for it instead of checking once. */
  const startWhenOn = () => {
    if (isOn()) return start();
    const classWatch = new MutationObserver(() => {
      if (!isOn()) return;
      classWatch.disconnect();
      start();
    });
    classWatch.observe(root, { attributes: true, attributeFilter: ['class'] });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startWhenOn, { once: true });
  else startWhenOn();
}
